import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import CookieConsent from "@/components/CookieConsent";
import CursorGlow from "@/components/CursorGlow";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import PageTransition from "@/components/PageTransition";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";

// Lazy-loaded routes — reduces initial JS on mobile
const Leistungen = lazy(() => import("./pages/Leistungen"));
const LeistungDetail = lazy(() => import("./pages/LeistungDetail"));
const Projekte = lazy(() => import("./pages/Projekte"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const MotorsportVideoproduktion = lazy(() => import("./pages/MotorsportVideoproduktion"));
const SportContent = lazy(() => import("./pages/SportContent"));
const Eventfilm = lazy(() => import("./pages/Eventfilm"));
const VideoproduktionBochum = lazy(() => import("./pages/VideoproduktionBochum"));
const VideoproduktionRuhrgebiet = lazy(() => import("./pages/VideoproduktionRuhrgebiet"));
const VideoproduktionNRW = lazy(() => import("./pages/VideoproduktionNRW"));
const ImagefilmBochum = lazy(() => import("./pages/ImagefilmBochum"));


const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <PageTransition key={location.pathname}>
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes location={location}>
            <Route path="/" element={<Index />} />
            <Route path="/leistungen" element={<Leistungen />} />
            <Route path="/leistungen/:slug" element={<LeistungDetail />} />
            <Route path="/projekte" element={<Projekte />} />
            <Route path="/ueber-uns" element={<UeberUns />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/motorsport-videoproduktion" element={<MotorsportVideoproduktion />} />
            <Route path="/sport-content" element={<SportContent />} />
            <Route path="/eventfilm" element={<Eventfilm />} />
            <Route path="/videoproduktion-bochum" element={<VideoproduktionBochum />} />
            <Route path="/videoproduktion-ruhrgebiet" element={<VideoproduktionRuhrgebiet />} />
            <Route path="/videoproduktion-nrw" element={<VideoproduktionNRW />} />
            <Route path="/imagefilm-bochum" element={<ImagefilmBochum />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </PageTransition>
    </>
  );
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CursorGlow />
      <BrowserRouter>
        <AnimatedRoutes />
        <WhatsAppFloat />
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
